import React, { useState } from "react";

export const STATUS_STYLES = {
  pending: { label: "Waiting for review", pill: "bg-amber-50 text-amber-800 ring-amber-200", dot: "bg-amber-500" },
  active: { label: "Active", pill: "bg-emerald-50 text-emerald-800 ring-emerald-200", dot: "bg-emerald-500" },
  suspended: { label: "Suspended", pill: "bg-slate-100 text-slate-700 ring-slate-300", dot: "bg-slate-400" },
};

export function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.suspended;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${style.pill}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${style.dot} ${status === "pending" ? "animate-pulse" : ""}`} />
      {style.label}
    </span>
  );
}

// Fallback colours for tenants without a brand colour, picked by name so each stays stable
const FALLBACK_COLORS = ["#4f46e5", "#0891b2", "#059669", "#d97706", "#db2777", "#7c3aed", "#2563eb", "#dc2626"];
export const brandColor = (tenant) => {
  const color = tenant?.brand?.color || tenant?.branding?.primaryColor;
  if (typeof color === "string" && /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(color)) return color;
  const name = tenant?.companyName || "";
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return FALLBACK_COLORS[hash % FALLBACK_COLORS.length];
};

const initials = (name = "") =>
  name
    .replace(/\b(pty|ltd|limited|inc|the|of|and)\b/gi, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

// Logo paths in branding are relative to the tenant's own site (e.g. /boitekaneloLogo.png)
const logoSrc = (tenant) => {
  const url = tenant?.brand?.logoUrl || tenant?.branding?.logoUrl;
  if (!url || typeof url !== "string") return null;
  if (/^https:\/\//i.test(url)) return url;
  if (url.startsWith("/") && tenant.domain) return `https://${tenant.domain}${url}`;
  return null;
};

const MARK_SIZES = {
  sm: "h-9 w-9 rounded-lg text-xs",
  lg: "h-16 w-16 rounded-2xl text-xl",
};

// The tenant's logo, or its initials on its brand colour if there's no logo (or it fails to load)
export function TenantMark({ tenant, size = "sm" }) {
  const [failed, setFailed] = useState(false);
  const src = logoSrc(tenant);
  const box = MARK_SIZES[size];
  if (src && !failed) {
    return (
      <span className={`flex shrink-0 items-center justify-center overflow-hidden bg-white ring-1 ring-slate-200 ${box}`}>
        <img src={src} alt="" className="h-full w-full object-contain p-1" onError={() => setFailed(true)} />
      </span>
    );
  }
  return (
    <span
      className={`flex shrink-0 items-center justify-center font-bold text-white shadow-sm ${box}`}
      style={{ background: brandColor(tenant) }}
      aria-hidden="true"
    >
      {initials(tenant?.companyName)}
    </span>
  );
}

export const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";

export const formatDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

export const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

// "3 days ago", "just now", or a date for anything older than a month
export const timeAgo = (iso) => {
  if (!iso) return "No activity yet";
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(iso);
};

export const QUIET_AFTER_DAYS = 30;

const STATUS_WORDS = { SELECTION_COMPLETE: "Selected", COMPLETED: "Monitored", ACTIVE_MANAGEMENT: "Active monitoring" };
export const riskStatusLabel = (status) => {
  if (STATUS_WORDS[status]) return STATUS_WORDS[status];
  const words = status.toLowerCase().split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const STAFF_ACTIONS = {
  SIGNED_IN: { verb: "signed in", tone: "bg-slate-300" },
  TENANT_APPROVED: { verb: "approved", tone: "bg-emerald-500" },
  TENANT_SUSPENDED: { verb: "suspended", tone: "bg-red-500" },
  TENANT_REACTIVATED: { verb: "reactivated", tone: "bg-emerald-500" },
  TENANT_REJECTED: { verb: "rejected and removed", tone: "bg-red-500" },
};

export const staffVerb = (action) => STAFF_ACTIONS[action]?.verb || action;
export const staffTone = (action) => STAFF_ACTIONS[action]?.tone || "bg-slate-300";

// Shared surfaces
export const CARD = "rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]";
